import React, { useState } from "react";

import Badge from "../../../../../../shared/components/design-system/Badge";
import Button from "../../../../../../shared/components/design-system/Button";
import LoadingSpinner from "../../../../../../shared/components/design-system/LoadingSpinner";
import ButtonSet from "../../../../../../shared/components/design-system/component-groups/ButtonSet";
import { SectionHeader } from "../../../../../../shared/components/design-system/component-groups/section-header-footer";
import ExportTableDataButton, { escape } from "../../../analytics/ExportTableDataButton";
import Card from "../../../design-system/Card";
import Select from "../../../design-system/Select";
import Table from "../../../design-system/Table";
import type { Row } from "./index";

export default function QuestionData({ rows: allRows, loading }: { rows: Row[]; loading: boolean }) {
  const [selectedQuestion, setSelectedQuestion] = useState<string>("");
  const [selectedOrganisationId, setSelectedOrganisationId] = useState<string>("");

  // Each selector's choices are constrained by the *other* facet, not itself
  const rowsMatchingQuestion = selectedQuestion
    ? allRows.filter((row) => row.questionTitle === selectedQuestion)
    : allRows;
  const rowsMatchingOrg = selectedOrganisationId
    ? allRows.filter((row) => row.organisationId === selectedOrganisationId)
    : allRows;

  const questionOptions = [...new Set(rowsMatchingOrg.map(({ questionTitle }) => questionTitle))].sort((a, b) =>
    a.localeCompare(b),
  );

  const organisationOptions = [
    ...new Map(rowsMatchingQuestion.map((row) => [row.organisationId, row.organisationName])),
  ].sort(([, nameA], [, nameB]) => nameA.localeCompare(nameB));

  // Options table applies both filters together
  const rowsForOptions = allRows.filter(
    (row) =>
      (!selectedQuestion || row.questionTitle === selectedQuestion) &&
      (!selectedOrganisationId || row.organisationId === selectedOrganisationId),
  );

  function toCsv(rows: typeof rowsForOptions) {
    return [
      ["Organisation", "Sector", "Question", "Option", "Tag", "QuestionType", "IsFreeText"],
      ...rows.map((row) => [
        escape(row.organisationName),
        escape(row.sector),
        escape(row.questionTitle),
        escape(row.optionValue),
        escape(row.tag ?? ""),
        escape(row.questionType ?? ""),
        row.isFreeText ? "true" : "false",
      ]),
    ];
  }

  if (loading) return <LoadingSpinner />;

  return (
    <>
      <Card className="ds-mt-5">
        <SectionHeader title="Filters">
          <ButtonSet>
            <Button
              label="Reset"
              icon="xmark"
              onClick={() => {
                setSelectedQuestion("");
                setSelectedOrganisationId("");
              }}
            />
          </ButtonSet>
        </SectionHeader>
        <Select
          disabled={allRows.length === 0}
          label="Question"
          value={selectedQuestion}
          onChange={setSelectedQuestion}
        >
          <option value="">All questions</option>
          {questionOptions.map((title, i) => (
            <option key={i} value={title}>
              {title}
            </option>
          ))}
        </Select>

        <Select
          className="ds-mt-5"
          disabled={allRows.length === 0}
          label="Organisation"
          value={selectedOrganisationId}
          onChange={setSelectedOrganisationId}
        >
          <option value="">All organisations</option>
          {organisationOptions.map(([id, name]) => (
            <option key={id} value={id}>
              {name}
            </option>
          ))}
        </Select>
      </Card>
      <Card>
        <SectionHeader title="Options">
          {" "}
          <ExportTableDataButton data={rowsForOptions.length ? toCsv(rowsForOptions) : undefined} />
        </SectionHeader>
        <Table
          data={rowsForOptions.map((da) => ({
            ...da,
            id: da.optionId,
          }))}
          columns={[
            {
              id: "option",
              label: "Option",
              element: (data) => <span>{data.optionValue}</span>,
            },
            {
              id: "tag",
              label: "Tag",
              element: (data) => (
                <span>
                  {data.tag ? (
                    <Badge variant="success" label={data.tag} />
                  ) : (
                    <Badge variant="warning" label="Untagged" />
                  )}
                </span>
              ),
            },
          ]}
        />
      </Card>
    </>
  );
}
